import { useSyncExternalStore } from 'react'
import { isNativeContainer } from './nativeContainer'
import { applyShellUpdate, getShellUpdateState, subscribeToShellUpdate } from './shellUpdate'
import { applyStoreUpdate, getStoreUpdateState, subscribeToStoreUpdate } from './storeUpdate'
import { getUpdateAlerts, subscribeToUpdateAlerts } from './updateAlerts'

/**
 * One hook over both update rails, and the one thing the mute is applied in (D266 §3.3).
 *
 * **The two rails are mutually exclusive by construction**, which is why this is a selection rather
 * than a merge: the shell rail needs a service worker the container deletes (D161(e)), and the
 * store rail needs `isNativeContainer()`. A browser can only ever be on the first; a store binary
 * can only ever be on the second.
 *
 * **`apply()` is the only caller of `applyShellUpdate()` and `applyStoreUpdate()`.** D137's rule —
 * *the only thing that sends `SKIP_WAITING` is the sidebar's Update row* — is unchanged in
 * substance and now reached through here, which is worth knowing before grepping for a caller the
 * rule does not appear to mention.
 *
 * **Both `useSyncExternalStore` calls run unconditionally**, and so does the preference's: the
 * rules of hooks do not permit selecting a store by branching, and the unused one costs a
 * subscription to a module that never publishes.
 */
export interface AppUpdate {
  /**
   * Something is available **and the resident wants to hear about it.** This is what raises the
   * hamburger dot and, on the PWA rail, the sidebar row.
   *
   * **D285 widened the preference and this field's rule is `!== 'mute'`, never `=== 'popover'`.**
   * The dot is D266's surface and the prompt is a second one on top of it, so the two loud answers
   * both raise it and only `mute` does not — writing this as an equality would silence the dot for
   * every resident who picked the *quieter* of the two ways of being told.
   */
  readonly available: boolean
  /** An apply is under way. Renames the row and refuses a second tap. */
  readonly applying: boolean
  /**
   * A `drawer` catalog key, so the Drawer stays one JSX block rather than three.
   *
   * `update` is *Update now* — the PWA's one-tap apply and Play's *start the download* tap alike;
   * `updateRestart` is FLEXIBLE's second tap, where the binary is on the device and the word the
   * resident presses has to be *Restart*; `updateInStore` is iOS, where the tap leaves the app.
   */
  readonly labelKey: 'update' | 'updateRestart' | 'updateInStore'
  apply(): void
}

/**
 * Whether the row itself renders, regardless of the mute.
 *
 * **Split out from `available` because the mute silences the dot and keeps the row** (§3.5), so
 * `App` needs the un-muted answer for the row while `AppBar` needs the muted one for the dot. It
 * is the same rail state read for two different questions.
 */
export interface AppUpdateRow extends AppUpdate {
  /** A newer version exists, mute or not. The sidebar row's own reason to exist. */
  readonly present: boolean
  /**
   * **D275 — the binary is on the device and the app has not been backgrounded since.** Android
   * installs it the moment the app leaves the foreground (`SeloGateStoreUpdatePlugin.handleOnPause`),
   * so this raises the one surface that exists for the resident who never leaves: Home's banner.
   *
   * **The mute is applied HERE and not at the consumer**, unlike `present`, which deliberately
   * escapes it. §3.5's rule is that the mute silences the *report* and keeps the *route*, and a
   * banner across the top of Home is the loudest report this rail has — louder than the dot it
   * already silences. The Update row stays reachable while muted, so nothing is taken away; the
   * resident is simply not told again on a screen they did not open for this. A muted resident's
   * update still installs on the next background, which is the whole point of D275 and needs no
   * surface at all.
   *
   * Always `false` on the shell rail: a waiting service worker has no downloaded/undownloaded
   * split, and the PWA's one tap has never had a second half to finish.
   */
  readonly installReady: boolean
  /**
   * **D285 — the launch prompt may be raised.** `App` owns when (once per launch, on Home, behind
   * the splash and behind D282's own prompt); this owns *whether*, which is the rail's state and
   * the preference's third answer.
   *
   * **It is a third reading of one rail state and not a third rail.** `present` asks *does a newer
   * version exist*, `available` asks *may the menu be marked*, and this asks *may the resident be
   * interrupted* — which is why the mute is applied here rather than at the consumer, exactly as it
   * is for `installReady`: a preference the loudest surface in the feature reads for itself is a
   * preference with an exception in it.
   *
   * **`applying` excludes it** rather than being left to `App`: a prompt offering to start what is
   * already running is the same false claim `labelKey` exists to prevent one row down.
   *
   * **And so does `installReady`, which is the other half of the same rule read across D275.** On
   * the store rail this is `phase === 'available'` and nothing wider: `downloading` is `applying`
   * above, and `downloaded` belongs to the banner **alone**. Two reasons, and the first is the
   * decision. D275's whole argument is that a downloaded update installs itself on the next
   * background and needs no action at all — so a modal is the wrong instrument for it twice over,
   * being an interruption raised for something optional, and the banner's own `updateReadyHint`
   * (*it installs by itself when you leave the app*) is the sentence that makes it optional. A
   * `ConfirmPopover` carries no such sentence, so at `downloaded` it would be a demand. The second
   * is that both surfaces answered at once: `promptable` read `phase !== 'downloading'`, and the
   * two are simultaneously true at `downloaded`, so the popover stood on top of the banner and
   * dismissing it revealed the same offer underneath — the *two launch-time cards on one screen*
   * cost D285 already refuses one prompt over, paid inside this feature.
   *
   * **It is a term here rather than a `!installReady` at the consumer** because the mute is applied
   * here for both fields, and a condition that decides which of two surfaces speaks belongs beside
   * the one that decides whether either does. The shell rail is untouched: `installReady` is
   * hard-coded `false` there, so its expression carries no such term and means what it always did.
   */
  readonly promptable: boolean
  /**
   * **D345 — the prompt may be raised IF the operator's soft floor says this build is urgent.**
   * `App` supplies that half (`config/`'s `useBuildBelowPromptFloor`), because this hook is
   * imported by `screens/home/` and may not reach `config/` — the open path's isolation rule.
   *
   * **It is the `menu` answer's reading of `promptable`, and nothing wider.** Every other term is
   * `promptable`'s own — the rail offering an update (`phase === 'available'`, which hands
   * `downloaded` to D275's banner and makes a soft floor above every shipped build prompt nobody)
   * — so the only thing D345 changes is which preference admits the prompt. **`mute` is not one of
   * them**: D266 §3.5's rule, that a feature does not get to force a notice about an update it will
   * not force, is the reason the soft floor is a notice and never pierces the mute. And `popover`
   * does not need it, `promptable` already carrying that answer for every update.
   *
   * Always `false` on the shell rail: the PWA carries no store build number (D330), so there is no
   * soft floor for it to be below (D345 (d)).
   */
  readonly promptableIfUrgent: boolean
}

export function useAppUpdate(): AppUpdateRow {
  const shell = useSyncExternalStore(subscribeToShellUpdate, getShellUpdateState, getShellUpdateState)
  const store = useSyncExternalStore(subscribeToStoreUpdate, getStoreUpdateState, getStoreUpdateState)
  const alerts = useSyncExternalStore(subscribeToUpdateAlerts, getUpdateAlerts, getUpdateAlerts)

  if (isNativeContainer()) {
    const present = store.phase !== 'idle'
    return {
      present,
      available: present && alerts !== 'mute',
      // **`phase === 'available'` and not `phase !== 'downloading'`** — see `promptable`. It
      // implies `present`, it implies `!applying`, and it is what hands `downloaded` to D275's
      // banner alone. iOS never leaves `available`, so that rail is unmoved.
      promptable: store.phase === 'available' && alerts === 'popover',
      promptableIfUrgent: store.phase === 'available' && alerts === 'menu',
      // Muted deliberately — see `installReady`. `downloaded` is Android's alone: the iOS rail
      // owns no download, so its action is `store` and its phase never reaches here.
      installReady: store.phase === 'downloaded' && alerts !== 'mute',
      // **FLEXIBLE's middle phase falls straight onto the Drawer's existing `updateApplying`
      // contract** — disabled, renamed to *Updating…* — with no new prop. That is the best single
      // reason to unify the two rails here rather than thread a second pair of props through
      // `App`.
      applying: store.phase === 'downloading',
      // **Keyed off `action`, never off `phase`.** The action is what the next tap *does*, which
      // is the only thing a label may claim: *Update now* on a row that restarts is a lie, and
      // reusing *Updating…* is worse — it says something is in flight when nothing is. The
      // `downloading` phase never reaches here, `applying` renaming the row above it.
      labelKey:
        store.action === 'restart' ? 'updateRestart' : store.action === 'store' ? 'updateInStore' : 'update',
      apply: () => void applyStoreUpdate(),
    }
  }

  return {
    present: shell.available,
    available: shell.available && alerts !== 'mute',
    promptable: shell.available && alerts === 'popover' && !shell.applying,
    promptableIfUrgent: false,
    installReady: false,
    applying: shell.applying,
    labelKey: 'update',
    apply: () => void applyShellUpdate(),
  }
}
